/* Import Global Libraries */
import { EventDispatcher, StreamEvent } from './Events';
import AudioPlayer from './views/AudioPlayer';
import VideoPlayer from './views/VideoPlayer';
import Connection from './Pair';
import Logger from './utils/Logger';
import GUMErrorHandler from './utils/ErrorHandler';
import customErrors from './customErrors';
import VcxEvent from './vcxEventProperties';
import localization from './Localization';
import customEvents from './customEvents';
import EL from './EventLogger';
import enxConfig from './../Common.config.js';
/*
 * Class: Stream
 * Description: This class represents a stream, either a Published Local Stream or Subscribed Remote Stream.
 *              This Class will handle WebRTC stream, identify it and draw it in Player.
 * Author: Subrat
 * Las Edit Date: 27/11/2017
 * Relase Version: 1.0
 */


const Stream = (altConnection, specInput) => {
  if (!enxConfig.is_supported()) { console.log('Webrtc not supported'); return undefined; }
  const spec = specInput;
  const that = EventDispatcher(spec);
  that.config = specInput;
  that.stream = spec.stream; // Media Stream
  that.url = spec.url; // URL - if a stream is initiated from a URL
  that.recording = spec.recording; // If a stream is initiated from a Recorded File.
  that.recordingId = undefined;
  that.room = undefined; // Gets room info when its published in a Room.
  that.showing = false; // If the Stream is being played in any DOM Object.
  that.local = false; // If the Stream is a local stream.
  that.video = spec.video; // If the stream has Video Track. Or, if a local stream has access to Video Device
  that.audio = spec.audio; // If the stream has Audio Track. Or, if a local stream has access to Mic Device
  that.screen = spec.screen; // If the stream has Screen Track. Or, if a local stream has access to Screen
  that.videoSize = spec.videoSize; // VideoSize to be defined in 4 element array [minWidth, minHeight, maxWidth, maxHeight]
  that.videoFrameRate = spec.videoFrameRate; // Video Frame Rate - Initiated or Opted, defined in 2 element array [min, max]
  that.extensionId = spec.extensionId; // Chrome Extension ID for Screen Share App
  that.desktopStreamId = spec.desktopStreamId; // Stream ID for Screen Share
  that.recordingId = spec.recordingId; // Archived File ID of recorded stream. Used for playback
  that.clientId = spec.clientId;
  that.videoMutedDueToBw = false; // If video track has been muted
  that.hardAudioMuted = false;
  that.hardVideoMuted = false;
  that.softVideoMuted = false;
  that.selfMuteVideo = false;
  that.selfMuteAudio = false;
  that.ATVideoMuted = false;
  that.selfPausedVideo = false;
  that.selfPausedAudio = false;
  that.processingVideoMute = false;
  that.attributes = spec.attributes; // Additional Information about Stream, defined as {var: value, var:value}
  that.Connection = altConnection === undefined ? Connection : altConnection;
  that.playerDivs = [];
  that.canvas = spec.canvas;
  that.transactions = new Map();
  that.audioStream = spec.audioStream;
  that.videoStream = spec.videoStream;
  that.recordedBlobs = [];
  const negotiatedMedia = { audio: spec.audio != undefined, video: spec.video != undefined };
  let audioMuted = false; // If audio track has been muted
  let videoMuted = false; // If video track has been muted
  that.abwdEnabled = true;
  that.abwdNotification = 'sdk';
  that.abwdLanguage = 'en';
  let currentMediaType = 'none';
  let currentRemoteClientId = 'none';
  let currentImageDisplay = false;
  let currentVideoMuteReason = 'none';
  let currentActiveTalkerList;
  that.maxVideoBwKbpsReqByUser = 0;
  that.maxVideoBwKbpsReqByServer = 0;
  that.maxVideoBwKbpsSet = 0;
  const mediaConfig = {
    videoFps: { set: 0, max: { user: 0, server: 0 }, min: { user: 0, server: 0 } },
    videoBw: { set: 0, max: { user: 0, server: 0 }, min: { user: 0, server: 0 } },
  };
  // Verify if video size specified in the specs is legible
  if (that.videoSize !== undefined && (!(that.videoSize instanceof Array) || that.videoSize.length !== 4)) {
    throw Error('Error! Invalid Video Size');
  }

  if (spec.local === undefined || spec.local === true) {
    that.local = true;
  } else {
    audioMuted = videoMuted = true; //remote streams set as false
  }

  that.isAudioMuted = () => audioMuted;
  that.isVideoMuted = () => videoMuted;
  // ************************************
  // Public function: getID()
  // It returns Unique Stream ID of Published and Subscribed Streams.
  // ************************************

  that.getID = () => {
    let id;
    // An initiated local stream will not have ID unless its published.
    if (that.local && !spec.streamID) {
      id = 'local'; // Inititing ID with a String Constant "Local"
    } else {
      id = spec.streamID;
    }
    return id;
  };


  // ************************************
  // Public function: getAttributes()
  // It returns attributes of the stream
  // ************************************

  that.getAttributes = () => spec.attributes;


  // ************************************
  // Public function: setAttributes()
  // It changes local & published stream attribute in room
  // ************************************

  that.setAttributes = (attrs) => {
    if (that.local) {
      that.emit(StreamEvent({ type: 'internal-set-attributes', stream: that, attrs }));
      return;
    }
    Logger.error('Error! Stream Object is not published. Failed to update attributes.');
  };


  // ************************************
  // Public function: updateLocalAttributes()
  // It changes local attributes of local or subscribed streams. Changes not reflected in Room
  // ************************************

  that.updateLocalAttributes = (attrs) => {
    spec.attributes = attrs;
  };


  // ************************************
  // Public function: ifAudio()
  // It returns if the Stream has Audio Track
  // ************************************

  that.ifAudio = () => {
    if (spec.audio !== false && spec.audio !== undefined) {
      return true;
    }

    return false;
  };


  // ************************************
  // Public function: ifVideo()
  // It returns if the Stream has Video Track
  // ************************************

  that.ifVideo = () => {
    if (spec.video !== false && spec.video !== undefined) {
      return true;
    }

    return false;
  };


  // ************************************
  // Public function: ifData()
  // It returns if the Stream has Data Track
  // ************************************

  that.ifData = () => {
    if (spec.data !== false && spec.data !== undefined) {
      return true;
    }

    return false;
  };


  // ************************************
  // Public function: ifScreen()
  // It returns if the Stream has Screen Share Track
  // ************************************

  that.ifScreen = () => spec.screen;


  // ************************************
  // Public function: ifMedia()
  // It returns if the Stream has either  Audio track,  Video track or Screen Track
  // ************************************

  that.ifMedia = () => {
    if (spec.audio !== false && spec.audio !== undefined) {
      return true;
    } else if (spec.video !== false && spec.video !== undefined) {
      return true;
    } else if (spec.screen !== false) {
      return true;
    }

    return false;
  };


  // ************************************
  // Public function: ifExternal()
  // It returns if the Stream Video Track is created from URL or a Recorded File
  // ************************************

  that.ifExternal = () => {
    if (that.url !== undefined || that.recording !== undefined) {
      return true;
    }

    return false;
  };


  that.ifCanvas = () => spec.canvas;

  // ************************************
  // Public function: sendData()
  // Sends plain text or mime encoded data.
  // This function will further evolve by changing parameter and Stream-Event to handle Public/Pvt Messages
  // ************************************

  that.sendData = (msg) => {
    if (that.local && that.ifData()) {
      that.emit(StreamEvent({ type: 'internal-send-data', stream: that, msg }));
      return;
    }
    Logger.error('Error! Stream Object is not published. Failed to send data.');
    EL.error('stream-event', customEvents.event_send_data_failed, { error: 'Error! Stream Object is not published. Failed to send data.' });
  };


  // ************************************
  // Public function: init()
  // Intializes stream, tries to  get access of Local Video and Audio Devices
  // This function must be called before one can publish stream into a room.
  // However, we don't want to shwocase this method to developers. Rather they would
  // Simply call an InitPublisher() function which will initiaze stream and do init(0 and
  // return the object
  // ************************************

  that.init = () => {
    try {
      Logger.info(`requested getUsermedia options: ${JSON.stringify(spec)}`);
      if ((spec.audio || spec.video || spec.screen || spec.canvas) || spec.url === undefined) {
        Logger.info('Note! Requested access to local media');
        let videoOpt = false,
          audioOpt = false,
          canvasOpt = false;
        const videoSizeForCanvas = [640, 480, 1280, 720];

        if (spec.video && (spec.video !== false)) {
          videoOpt = {};
          if (that.videoSize !== undefined) {
            /*videoOpt = { width : {min: that.videoSize[0], max: that.videoSize[2], ideal: that.videoSize[2]},
                         height: {min:that.videoSize[1], max: that.videoSize[3], ideal:that.videoSize[3]}};*/
            videoOpt = { width: { max: that.videoSize[2] }, height: { max: that.videoSize[3] } };
            if (enxConfig.browser_info.device_type != 'mobile') {
              videoOpt.width.min = that.videoSize[0];
              videoOpt.height.min = that.videoSize[1];
              if (Connection.browserEngineCheck() === 'chrome-stable') {
                videoOpt.width.ideal = that.videoSize[2];
                videoOpt.height.ideal = that.videoSize[3];
              }
            } else {
              videoOpt.width.ideal = that.videoSize[2];
              videoOpt.height.ideal = that.videoSize[3];
            }
          }
          if (that.videoFrameRate !== undefined) { videoOpt.frameRate = { min: that.videoFrameRate[0], max: that.videoFrameRate[1] }; }
          if (typeof spec.video === 'object') {
            if (spec.video.deviceId !== undefined && spec.video.deviceId !== '') {
              videoOpt.deviceId = (Connection.browserEngineCheck() !== 'safari') ?
                { exact: spec.video.deviceId } : spec.video.deviceId;
            } else if (spec.video.facingMode !== undefined && spec.video.facingMode !== '' &&
              enxConfig.browser_info.device_type == 'mobile') {
              videoOpt.facingMode = spec.video.facingMode;
            }
          }
        }

        if (spec.screen && (spec.screen !== false)) {
          videoOpt = true;
        }

        if (spec.canvas && spec.canvas !== false) {
          canvasOpt = {};
          if (that.videoSize === undefined) {
            canvasOpt = {
              width: { min: that.videoSizeForCanvas[0], max: that.videoSizeForCanvas[2] },
              height: { min: that.videoSizeForCanvas[1], max: that.videoSizeForCanvas[3] },
              canvasId: spec.div,
              fps: spec.fps,
            };
          } else {
            canvasOpt = {
              width: { min: that.videoSize[0], max: that.videoSize[2] },
              height: { min: that.videoSize[1], max: that.videoSize[3] },
              canvasId: spec.div,
              fps: spec.fps,
            };
          }
        }

        if (spec.audio && (spec.audio !== false)) {
          audioOpt = {
            echoCancellation: { exact: true },
            googEchoCancellation: { exact: true },
            autoGainControl: { exact: true },
            noiseSuppression: { exact: true },
            googHighpassFilter: { exact: true },
            googExperimentalNoiseSuppression: { exact: true },
            googExperimentalEchoCancellation: { exact: true },
            googExperimentalAutoGainControl: { exact: true },
          };
          if ((typeof spec.audio === 'object') && spec.audio.deviceId && spec.audio.deviceId !== '') {
            audioOpt.deviceId = { exact: spec.audio.deviceId };
          }
        }

        const opt = {
          video: videoOpt,
          audio: audioOpt,
          screen: spec.screen,
          canvas: canvasOpt,
          fake: spec.fake,
          extensionId: that.extensionId,
          desktopStreamId: that.desktopStreamId,
        };
        if (spec.screen && (spec.screen !== false)) {
          opt.fps = spec.fps;
        }

        if (spec.canvas && (spec.canvas !== false)) {
          that.Connection.GetCanvasStream(opt, (stream) => {
            that.videoStream = stream;
            that.stream = stream;
            console.log(`Got stream from canvas opt: ${opt} refreshFn: ${spec.refreshFn}`);
            that.videoStream.getTracks().forEach((trackInput) => {
              Logger.info('getTracks canvas: ', trackInput);
              const track = trackInput;
              track.onended = () => {
                that.videoStream.getTracks().forEach((secondTrackInput) => {
                  const secondTrack = secondTrackInput;
                  secondTrack.onended = null;
                });

                const streamEvent = StreamEvent({
                  type: 'stream-ended',
                  stream: that,
                  msg: track.kind,
                });
                that.dispatchEvent(streamEvent);
              };
            });
            const streamEvent = StreamEvent({ type: 'media-access-allowed', stream: that });
            that.dispatchEvent(streamEvent);
            EL.info('stream-event', customEvents.event_media_access_success, { error: {} });
          }, (err) => {
            Logger.error('GetCanvasStream: ', err);
            const streamEvent = StreamEvent({ type: 'media-access-denied', msg: err });
            that.dispatchEvent(streamEvent);
            EL.error('stream-event', customEvents.event_media_access_denied, { error: err });
          });
        } else {
          Logger.info(`Note!! calling getUserMedia ${JSON.stringify(opt)}`);
          getUserMedia(opt, (stream, error) => {
            if (!error) {
              Logger.info('Note! User has granted access to local media.');
              EL.info('stream-event', customEvents.event_media_access_success, { error: {} });
              that.stream = stream;
              that.stream.getTracks().forEach((trackInput, index) => {
                if (trackInput.kind === 'video') {
                  that.videoStream = stream;
                  if (typeof (spec.video) !== 'object') spec.video = {};
                  spec.video.trackIndex = index;
                  if (trackInput.getSettings().deviceId) spec.video.deviceId = trackInput.getSettings().deviceId;
                  console.log(` Checking and setting soft video muted : ${that.config.videoMuted} firstGumcall: ${that.firstGumCall}`);
                  if (that.config.videoMuted && that.firstGumCall === undefined) {
                    trackInput.enabled = false;
                    console.log(' soft muted video ');
                  }
                } else if (trackInput.kind === 'audio') {
                  that.audioStream = stream;
                  if (typeof (spec.audio) !== 'object') spec.audio = {};
                  spec.audio.trackIndex = index;
                  if (trackInput.getSettings().deviceId) spec.audio.deviceId = trackInput.getSettings().deviceId;
                  if (that.config.audioMuted && that.firstGumCall === undefined) {
                    trackInput.enabled = false;
                    console.log(' soft muted aduio');
                  }
                }
                Logger.info('getTracks', trackInput, ' kind: ', trackInput.kind);
                const track = trackInput;
                track.onended = () => {
                  that.stream.getTracks().forEach((secondTrackInput) => {
                    const secondTrack = secondTrackInput;
                    secondTrack.onended = null;
                  });

                  const streamEvent = StreamEvent({
                    type: 'stream-ended',
                    stream: that,
                    msg: track.kind,
                  });

                  that.dispatchEvent(streamEvent);
                };
              });
              if(that.videoStream == null) {
                that.video = false;
                that.config.videoMuted = true;
              }
              if(that.audioStream == null)  {
                that.audio = false;
                that.config.audioMuted = true;
              }
              const streamEvent = StreamEvent({ type: 'media-access-allowed', stream: that });
              that.dispatchEvent(streamEvent);
              EL.info('stream-event', customEvents.event_media_access_success, { error: {} });
            } else {
              Logger.error(`Error! Failed to get access to local media. Error code was ${JSON.stringify(error)}.`);
              const streamEvent = StreamEvent({ type: 'media-access-denied', msg: error });
              that.dispatchEvent(streamEvent);
              EL.error('stream-event', customEvents.event_media_access_denied, { error });
            }
            that.firstGumCall = true;
          });
        }
      } else {
        const streamEvent = StreamEvent({ type: 'media-access-allowed' });
        that.dispatchEvent(streamEvent);
        EL.info('stream-event', customEvents.event_media_access_success, { error: {} });
      }
    } catch (e) {
      Logger.error(`Error! Failed to get access to local media. Error was ${e}.`);
      const streamEvent = StreamEvent({ type: 'media-access-denied', msg: e });
      that.dispatchEvent(streamEvent);
      EL.error('stream-event', customEvents.event_media_access_denied, { error: e });
    }
  };

  that.initRemoteStream = (stream) => {
    if (!that.local) {
      that.stream = stream;
      if (that.ifAudio() || that.audioOnly) {
        that.audioStream = new MediaStream();
        that.stream.getAudioTracks().forEach((track) => {
          that.audioStream.addTrack(track);
        });
      }
      // @todo - mediaStream or videoStream
      that.mediaStream = new MediaStream();
      that.stream.getTracks().forEach((track) => {
        that.mediaStream.addTrack(track);
      });
    }
  };

  // ************************************
  // Public function: close()
  // Unpublishes a published stream from Room. Hides it from HTML DOM object and stops each track of the stream.
  // ************************************

  that.close = () => {
    if (that.local) {
      // Unpublishes stream from Room
      if (that.room !== undefined) {
        that.room.unpublish(that);
      }

      // Removes HTML DOM element
      that.hide();
      if (that.videoStream !== undefined) {
        that.videoStream.getTracks().forEach((trackInput) => {
          const track = trackInput;
          track.onended = null;
          track.stop(); // Stops each track in the Stream
        });
        that.videoStream = undefined;
      }
      if (that.audioStream !== undefined) {
        that.audioStream.getTracks().forEach((trackInput) => {
          const track = trackInput;
          track.onended = null;
          track.stop(); // Stops each track in the Stream
        });
        that.audioStream = undefined;
      }
      // @todo - no clean up for mediaStream object
      if (that.pc && that.pc.peerConnection !== undefined)
        that.pc.peerConnection.close();
      that.stream = undefined;
    }
  };


  // ************************************
  // Public function: play()
  // Plays the stream in HTML DOM Element. One can pass all player's display options too.
  // ************************************
  that.disableTrack = (streamID) => {
    that.room.removeTrack(streamID);
  };

  that.attachMediaStream = (element, stream) => {
    if (!element) {
      return null;
    }
    if (element.isWebRtcPlugin) {
      element.src = stream;
      return element;
    } else if (element.nodeName.toLowerCase() === 'video') {
      Logger.info('Checked element is video');
      if (!element.pluginObj && stream) {
        Logger.info('Plugins and stream validated');
        const _pluginObj = document.createElement('object');
        const _isIE = (Object.getOwnPropertyDescriptor && Object.getOwnPropertyDescriptor(window, 'ActiveXObject')) || ('ActiveXObject' in window);
        if (_isIE) {
          Logger.info('Browser IE validated');
          // windowless
          const windowlessParam = document.createElement('param');
          windowlessParam.setAttribute('name', 'windowless');
          windowlessParam.setAttribute('value', true);
          _pluginObj.appendChild(windowlessParam);
          _pluginObj.setAttribute('classid', 'CLSID:7FD49E23-C8D7-4C4F-93A1-F7EACFA1EC53');
        } else {
          Logger.info('Other browser got');
          _pluginObj.setAttribute('type', 'application/webrtc-everywhere');
        }
        element.pluginObj = _pluginObj;

        _pluginObj.setAttribute('className', element.className);
        _pluginObj.setAttribute('innerHTML', element.innerHTML);
        let width = element.getAttribute('width');
        let height = element.getAttribute('height');
        const bounds = element.getBoundingClientRect();
        if (!width) width = bounds.right - bounds.left;
        if (!height) height = bounds.bottom - bounds.top;

        if ('getComputedStyle' in window) {
          Logger.info('computing style');
          const computedStyle = window.getComputedStyle(element, null);
          if (!width && computedStyle.width != 'auto' && computedStyle.width != '0px') {
            width = computedStyle.width;
          }
          if (!height && computedStyle.height != 'auto' && computedStyle.height != '0px') {
            height = computedStyle.height;
          }
        }
        if (width) _pluginObj.setAttribute('width', width);
        else _pluginObj.setAttribute('autowidth', true);
        if (height) _pluginObj.setAttribute('height', height);
        else _pluginObj.setAttribute('autoheight', true);

        document.body.appendChild(_pluginObj);
        if (element.parentNode) {
          Logger.info('parent node found');
          element.parentNode.replaceChild(_pluginObj, element); // replace (and remove) element
          // add element again to be sure any query() will succeed
          document.body.appendChild(element);
          element.style.visibility = 'hidden';
        }
      }


      if (element.pluginObj) {
        Logger.info('plugin object found');
        element.pluginObj.bindEventListener('play', (objvid) => {
          if (element.pluginObj) {
            if (element.pluginObj.getAttribute('autowidth') && objvid.videoWidth) {
              element.pluginObj.setAttribute('width', objvid.videoWidth/* + "px"*/);
            }
            if (element.pluginObj.getAttribute('autoheight') && objvid.videoHeight) {
              element.pluginObj.setAttribute('height', objvid.videoHeight/* + "px"*/);
            }
          }
        });
        element.pluginObj.src = stream;
      }
      return element.pluginObj;
    } else if (element.nodeName.toLowerCase() === 'audio') {
      return element;
    }
    // @todo - element.nodeName can be only audio or video?
  };
  let retryCount = 1;

  const getUserMedia = (opt, callback) => {
    Logger.info(`getusermedia called  opt ${JSON.stringify(opt)}`);
    that.Connection.GetUserMedia(opt, (stream) => {
      Logger.info('getUserMedia success');
      EL.info('stream-event', customEvents.event_gum_success, { error: {} });
      callback(stream);
    }, (error) => {
      error = GUMErrorHandler(error);
      let retry = true;

      if ((opt.video.height && opt.video.height.min) && (opt.video.width && opt.video.width.min)) {
        Logger.info(`getUserMedia failed error: ${error.result} min Res: ${opt.video.height.min}X${opt.video.width.min}`);
        EL.error('stream-event', customEvents.event_gum_failed, { error });
      }
      const newOpt = {};
      Object.assign(newOpt, opt);
      switch (error.result) {
        case 1143:
        case 1144:
          console.log(` ERROR : ${error.result} newOpt: ${JSON.stringify(newOpt)}`);
          if (newOpt.video && newOpt.audio) {
            console.log('getUserMedia() Failed with device denied/unvailable .. retrying with audio only');
            newOpt.video = false;
          } else {
            retry = false;
          }
          break;
        case 1147:
        case 1148:
        case 1149:
          switch (newOpt.video.height.min) {
            case 720:
              newOpt.video.height.min = 540;
              newOpt.video.width.min = 960;
              break;
            case 540:
              newOpt.video.height.min = 540;
              newOpt.video.width.min = 960;
              break;
            case 480:
              newOpt.video.height.min = 360;
              newOpt.video.width.min = 480;
              break;
            default:
              retry = false;
              break;
          }
          break;
        default:
          console.log(` ERROR Default : ${error.result} newOpt: ${JSON.stringify(newOpt)}`);
          retry = false;
          break;
      }
      if (retry === true && retryCount <= 30) {
        retryCount = retryCount + 1;
        Logger.info("getUserMedia failed retrying..");
        getUserMedia(newOpt, callback);

      } else {
        callback(undefined, error);
      }
    });
  };

  that.setQualityLayers = (numSpatialLayers) => {
    if (that.pc && that.stream && that.local && that.pc.peerConnection && that.pc.peerConnection.getSenders) {
      Logger.info(` that.setQulaityLayers numSpatialLayers:${numSpatialLayers}`);

      that.pc.peerConnection.getSenders().forEach((sender) => {
        if (sender.track && sender.track.kind === 'video') {
          const parameters = sender.getParameters();
          if ((parameters != undefined) && (parameters.encodings != undefined)) {
            for (let cnt = 0; cnt < parameters.encodings.length; cnt++) {
              if (numSpatialLayers > cnt) {
                Logger.info(` Layer :${cnt} enabled`);
                parameters.encodings[cnt].active = true;
              } else {
                Logger.info(` Layer :${cnt} disabled`);
                parameters.encodings[cnt].active = false;
              }
            }
            sender.setParameters(parameters);
          } else {
            Logger.info(' that.setQulaityLayers: no sender params/encodings-  Not in simulcast mod');
          }
        }
      });
    } else {
      Logger.info(`setQulaityLayers Failed invalid stream :${that.stream} local: ${that.local}`);
    }
  };

  that.onStateChanged = (active) => {
    console.log(` that.onStateChanged() active : ${active} spec:${JSON.stringify(spec)}`);
    if (that.local) {
      if (active) {
        if (that.ifCanvas()) {
          if (spec.refreshFn !== undefined) {
            spec.refreshFn();
            const timerVal = spec.fps > spec.maxCanvasRefreshRate ? 1000 / spec.maxCanvasRefreshRate : 1000 / spec.fps;
            console.log(` that.onStateChanged ()  timerVal : ${timerVal} spec.fps: ${spec.fps}`);
            that.refreshTimer = setInterval(() => { console.log('refresh called timer '); spec.refreshFn(); }, timerVal);
          }
        } else if (!that.ifScreen() && that.video === true) {
          negotiatedMedia.video = !!that.videoStream;
          negotiatedMedia.audio = !!that.audioStream;
          if (that.maxVideoLayers <= 0) {
            //setVideoBitrate();
            mediaConfig.videoBw.set = mediaConfig.videoFps.set = 0;
            setVideoParams(mediaConfig.videoBw, mediaConfig.videoFps);
          } else {
            that.videoStream.getVideoTracks().forEach((track) => {
              const constraints = track.getConstraints();
              constraints.height.max = that.videoSize[3];
              constraints.height.ideal = that.videoSize[3];
              constraints.width.max = that.videoSize[2];
              constraints.width.ideal = that.videoSize[2];
              constraints.frameRate = { max: 26 };
              track.applyConstraints(constraints)
                .then(() => { console.log('applying constraints for simulcast video success'); })
                .catch((e) => { console.log(` error in applying constraints for simulcast video error: ${e}`); });
            });
          }
        }
      } else if (that.ifCanvas() && that.refreshTimer != undefined) {
        clearInterval(that.refreshTimer);
        that.refreshTimer = undefined;
      }
    }
  };

  that.setVideoParamsRange = (maxKbps, minKbps, maxFps, minFps, setByServer) => {
    if (that.maxVideoLayers > 1) return;

    if (setByServer) {
      if (maxKbps !== undefined) mediaConfig.videoBw.max.server = parseInt(maxKbps);
      if (maxFps !== undefined) mediaConfig.videoFps.max.server = parseInt(maxFps);
      if (minKbps !== undefined) mediaConfig.videoBw.min.server = parseInt(minKbps);
      if (minFps !== undefined) mediaConfig.videoFps.min.server = parseInt(minFps);
    } else {
      if (maxKbps !== undefined) mediaConfig.videoBw.max.user = parseInt(maxKbps);
      if (maxFps !== undefined) mediaConfig.videoFps.max.user = parseInt(maxFps);
      if (minKbps !== undefined) mediaConfig.videoBw.min.user = parseInt(minKbps);
      if (minFps !== undefined) mediaConfig.videoFps.min.user = parseInt(minFps);
    }
    Logger.debug(`setVideoParamsRange() id: ${that.getID()} :: ${maxKbps}: ${minKbps}:${maxFps}:${minFps}:${setByServer}`);
    setVideoParams(mediaConfig.videoBw, mediaConfig.videoFps);
  };

  const setVideoParams = (bwRange, fpsRange) => {
    let b2Set = 0,
      fps2Set = 0;
    if (bwRange.max.user && bwRange.max.user < bwRange.min.user) b2Set = bwRange.min.user;
    else if (bwRange.max.user && (!bwRange.max.server || bwRange.max.user <= bwRange.max.server)) b2Set = bwRange.max.user;
    else if (bwRange.max.server && bwRange.max.server < bwRange.min.user) b2Set = bwRange.min.user;
    else if (bwRange.max.server && bwRange.max.server < bwRange.min.server) b2Set = bwRange.min.server;
    else b2Set = bwRange.max.server;

    if (fpsRange.max.user < fpsRange.min.user) fps2Set = fpsRange.min.user;
    else if (fpsRange.max.user) fps2Set = fpsRange.max.user;
    else if (fpsRange.max.server && fpsRange.max.server < fpsRange.min.user) fps2Set = fpsRange.min.user;
    else if (fpsRange.max.server && fpsRange.max.server < fpsRange.min.server) fps2Set = fpsRange.min.server;
    else fps2Set = fpsRange.max.server;

    if (that.pc == undefined || that.pc.peerConnection == undefined || that.room == undefined) {
      Logger.error(`setVideoParams() not set pc/room not defined ${JSON.stringify(mediaConfig)} b2Set: ${b2Set} fps2Set: ${fps2Set}`);
      return;
    }

    if (b2Set && (bwRange.min.server != b2Set && bwRange.max.server != b2Set)) {
      const options = [{
        id: 'publisher-video-bw-update',
        streamId: that.getID(),
        min: bwRange.min.user,
        max: bwRange.max.user,
      }];
      that.room.setAdvancedOptions(options, () => { });
    }
    console.log(` setVideoParams: id: ${that.getID()} b2Set ${b2Set} bwrange: ${JSON.stringify(bwRange)}`);
    if ((b2Set && bwRange.set != b2Set) || (fps2Set && fpsRange.set != fps2Set)) {
      const browser = Connection.browserEngineCheck();
      if ((browser === 'chrome-stable') || ((browser === 'mozilla') &&
        (!Connection.isCurrentVerLess(VcxEvent.constant.FIREFOX_VERSION_SENDER_ENCODINGS_PARAM_SUPPORT))) ||
        (browser === 'safari' &&
          !Connection.isCurrentVerLess(VcxEvent.constant.SAFARI_VERSION_SENDER_ENCODINGS_PARAM_SUPPORT))) {
        that.pc.peerConnection.getSenders().forEach((sender) => {
          if (sender.track && sender.track.kind === 'video') {
            const parameters = sender.getParameters();
            if (parameters != undefined) {
              if (!parameters.encodings) parameters.encodings = [{}];
              for (let cnt = 0; cnt < parameters.encodings.length; cnt++) {
                if (b2Set) {
                  Logger.error(' that.setMaxVideoBitarte: bw: ', b2Set);
                  parameters.encodings[cnt].maxBitrate = b2Set * 1000;
                  bwRange.set = b2Set;
                }
                if (fps2Set) {
                  Logger.error(' that.setMaxVideoFramerate: fps: ', fps2Set);
                  parameters.encodings[cnt].maxFramerate = fps2Set;
                  fpsRange.set = fps2Set;
                }
              }
              sender.setParameters(parameters);
            } else {
              Logger.error(' that.setMaxVideoBitarte: no sender params/encodings-  ');
            }
          }
        });

        const setVideoQualityContraints = (track, constraints, resTable, typeIndex, resIndex, fps, errors, callback) => {
          if (typeIndex < resTable.length) {
            if (constraints.height)
              constraints.height.max = resTable[typeIndex].res[resIndex].height;
            //constraints.height.ideal = resTable[typeIndex].res[resIndex].height ;
            if (constraints.width)
              constraints.width.max = resTable[typeIndex].res[resIndex].width;
            //constraints.width.ideal = resTable[typeIndex].res[resIndex].width;
            constraints.frameRate = { max: fps };
            track.applyConstraints(constraints)
              .then(() => {
                callback(constraints, errors);
              })
              .catch((e) => {
                //console.log("RESOLUTION Set track constariants failed: " + e + " constraints: " + JSON.stringify(constraints));
                errors.push({ res: resTable[typeIndex].res[resIndex], error: e });
                if (resTable[typeIndex].res.length < (resIndex + 1)) resIndex++;
                else { typeIndex++; resIndex = 0, fps = 30; }
                // only first time take the fps value, after that should be 30
                setVideoQualityContraints(track, constraints, resTable, typeIndex, resIndex, fps, errors, callback);
              });
          } else {
            callback(null, errors);
          }
        };

        if (!that.ifScreen() && !that.ifCanvas() && browser !== 'safari') {
          that.videoStream.getVideoTracks().forEach((track) => {
            const quality = enxConfig.getCamResTableToCheck(bwRange.set);
            //console.log (" Bitrate map table : " + JSON.stringify(enxConfig.getBitrateMapTable()) +
            //             " res table: " + JSON.stringify(enxConfig.getCamResolutionTable()));
            const constraints = track.getConstraints();
            if (quality == undefined) {
              console.log(`reverting kbps: ${bwRange.set} constraints: curr: ${JSON.stringify(constraints)
                } original: ${that.videoSize[3]} X ${that.videoSize[2]}`);
              if (that.videoSize[3] != constraints.height.max || that.videoSize[2] != constraints.width.max) {
                constraints.height.max = that.videoSize[3];
                constraints.height.ideal = that.videoSize[3];
                constraints.width.max = that.videoSize[2];
                constraints.width.ideal = that.videoSize[2];
                constraints.frameRate = { max: 26 };
                track.applyConstraints(constraints)
                  .then(() => { console.log('apply constraints to original value'); })
                  .catch((e) => { console.log('failed  - apply constraints to original value'); });
              }
            } else {
              //console.log (" quality received: " + JSON.stringify(quality));
              const errors = [];
              setVideoQualityContraints(
                track, constraints, quality.resTable, 0, 0, quality.fps, errors,
                (constraints, errors) => {
                  if (constraints != null) {
                    console.log(`setVideoQualityContraints() success constraints: ${JSON.stringify(constraints)
                      } errors: ${JSON.stringify(errors)}`);
                  } else {
                    console.log(`setVideoQualityContraints() failed errors: ${JSON.stringify(errors)}`);
                  }
                },
              );
            }
          });
        }
      } else {
        //support traditional way of sdp renegotiation.. in kbps
        Logger.error('Setting Max Bw  using sdp reneg videoBwKbps= ', videoBwKbps);
        const config = { maxVideoBW: b2Set, Sdp: true };
        bwRange.set = b2Set;
        that.pc.updateSpec(config, () => {
          Logger.info('that.setMaxVideoBitarte: sdp reneg success');
        });
        EL.warn('stream-event', customEvents.event_incompatible_browser, { message: (`Setting Max Bw  using traditional way of sdp renegotiation videoBwKbps= ${b2Set}`) });
      }
    } else {
      Logger.error(`setVideoParams Failed id: ${that.getID()} b2Set ${b2Set} bwrange: ${JSON.stringify(bwRange)}`);
      EL.error('stream-event', customEvents.event_general_failed, { message: (`setVideoParams Failed id: ${that.getID()} b2Set ${b2Set} bwrange: ${JSON.stringify(bwRange)}`) });
    }
  };

  that.setVideoResolution = (width, height, fps) => {
    that.videoStream.getVideoTracks().forEach((track) => {
      const constraints = track.getConstraints();
      console.log(`RESOLUTION Set track before constariants : ${JSON.stringify(constraints)}`);
      constraints.height.max = height;
      //constraints.height.ideal = height ;
      constraints.width.max = width;
      //constraints.width.ideal = width;
      constraints.frameRate = { max: fps };
      track.applyConstraints(constraints)
        .then(() => {
          console.log(`RESOLUTION Set track constariants success : ${JSON.stringify(constraints)}`);
        })
        .catch((reason) => {
          Logger.error(`RESOLUTION Set trac constraints failed: ${reason} constraints: ${JSON.stringify(constraints)}`);
          EL.error('stream-event', customEvents.event_general_failed, { message: (`RESOLUTION Set trac constraints failed: ${reason} constraints: ${JSON.stringify(constraints)}`) });
        });
    });
  };

  that.switchMicrophone = (stream, mic_id, callback) => {
    if (!negotiatedMedia.audio) {
      EL.error('stream-event', customEvents.event_general_failed, { message: ('switchMicrophone - Stream not found') });
      callback(customErrors.error_1159);
      return;
    }
    if (that.selfMuteAudio || that.hardAudioMuted) {
      EL.warn('stream-event', customEvents.event_general_failed, { message: ('switchMicrophone - Media device switching not allowed in mute state') });
      callback(customErrors.error_1189);
      return;
    }
    if (setTrasactionIfNotExists('setMicrophoneTrack') == false) {
      EL.warn('stream-event', customEvents.event_general_failed, { message: ('switchMicrophone - Previous similar request in process') });
      callback(customErrors.error_1140);
      return;
    }
    setMediaDevicesTrack(stream, mic_id, undefined, (status) => {
      resetTransaction('setMicrophoneTrack');
      callback((status.result === 0) ? stream : status);
    });
  };

  that.switchCamera = (stream, camInfo, callback = () => { }) => {
    if (typeof callback === 'function') {
      if (that.stream == undefined) {
        callback(customErrors.error_1159);
        resetTransaction("setCameraTrack");
        return;
      }
      if (that.stream.getVideoTracks().length !== 1) {
        callback(customErrors.error_1159);
        resetTransaction("setCameraTrack");
        return;
      }
      if (!negotiatedMedia.video) {
        EL.error('stream-event', customEvents.event_general_failed, { message: ('switchCamera - Stream not found') });
        callback(customErrors.error_1159);
        return;
      }
      if (that.selfMuteVideo || that.hardVideoMuted) {
        EL.warn('stream-event', customEvents.event_general_failed, { message: ('switchCamera - Media device switching not allowed in mute state') });
        callback(customErrors.error_1189);
        return;
      }
      if (setTrasactionIfNotExists('setCameraTrack') == false) {
        EL.warn('stream-event', customEvents.event_general_failed, { message: ('selfMuteVideo - Previous similar request in process') });
        callback(customErrors.error_1140);
        resetTransaction("setCameraTrack");
        return;
      }
      const cam = {};
      if (camInfo != undefined) {
        if (typeof camInfo === 'string') cam.deviceId = camInfo;
        else if (typeof camInfo === 'object') {
          if (camInfo.deviceId != undefined && typeof camInfo.deviceId === 'string') cam.deviceId = camInfo.deviceId;
          else if (camInfo.facingMode != undefined && typeof camInfo.facingMode === 'string') { cam.facingMode = camInfo.facingMode; } else {
            Logger.error(`Error switchCamera(), error: invalid camera options ${JSON.stringify(camInfo)}`);
            EL.error('stream-event', customEvents.event_general_failed, { message: (`Error switchCamera(), error: invalid camera options ${JSON.stringify(camInfo)}`) });
            callback(customErrors.error_1155);
            return;
          }
        } else {
          Logger.error(`Error switchCamera(), error: invalid camera options type ${JSON.stringify(camInfo)}`);
          EL.error('stream-event', customEvents.event_general_failed, { message: (`Error switchCamera(), error: invalid camera options type ${JSON.stringify(camInfo)}`) });
          callback(customErrors.error_1155);
          resetTransaction("setCameraTrack");
          return;
        }
      } else {
        Logger.error('Error switchCamera(), error: camera options undefined');
        EL.error('stream-event', customEvents.event_general_failed, { message: ('Error switchCamera(), error: camera options undefined') });
        callback(customErrors.error_1155);
        resetTransaction("setCameraTrack");
        return;
      }
      if (Connection.getAndroidVersion() == 11 && Connection.browserEngineCheck() == 'chrome-stable') {
        callback(customErrors.error_1153);
        resetTransaction("setCameraTrack");
        return;
      }
      var retry = 0;

      that.stream.getVideoTracks().forEach((track) => {
        track.stop();
        track.onended = setMediaDevicesTrack(stream, undefined, cam, (status) => {
          var stream_status = false;
          var callback_stream = stream;
          stream.stream.getVideoTracks().forEach((track) => {
            stream_status = track.readyState;
          })
          if (status.result === 0) {
            attachStreamOnPlayerDivs(that.videoStream);
            resetTransaction("setCameraTrack");
            callback((status.result === 0) ? stream : status);
          }
          if (status.result === 1150) {
            // attachStreamOnPlayerDivs(that.videoStream);
            // callback(stream);
            if (stream_status == 'live') {
              attachStreamOnPlayerDivs(that.videoStream);
              resetTransaction("setCameraTrack");
              callback(stream);
            }
            else {
              resetTransaction("setCameraTrack");
              callback(status);

            }
          }
          else {
            resetTransaction("setCameraTrack");
            callback(status);
          }

        });
      });
    }
    else {
      Logger.error('switchCamera() invalid param - callback');
    }
    // resetTransaction("setCameraTrack");
  };

  that.switchMediaDevices = (stream, mic_id, camInfo, callback) => {
    if (!negotiatedMedia.audio || !negotiatedMedia.video) {
      EL.error('stream-event', customEvents.event_general_failed, { message: ('switchMediaDevices - Stream not found') });
      callback(customErrors.error_1159);
      return;
    }
    if (camInfo !== undefined) {
      if (that.selfMuteVideo || that.hardVideoMuted) {
        EL.warn('stream-event', customEvents.event_general_failed, { message: ('switchMediaDevices - Media device switching not allowed in video mute state') });
        callback(customErrors.error_1189);
        return;
      }
      if (setTrasactionIfNotExists('setCameraTrack') == false) {
        EL.warn('stream-event', customEvents.event_general_failed, { message: ('switchMediaDevices - Previous similar request in process') });
        callback(customErrors.error_1140);
        return;
      }
    }
    if (mic_id !== undefined) {
      if (that.selfMuteAudio || that.hardAudioMuted) {
        EL.warn('stream-event', customEvents.event_general_failed, { message: ('switchMediaDevices - Media device switching not allowed in audio mute state') });
        callback(customErrors.error_1189);
        if (camInfo !== undefined) resetTransaction('setCameraTrack');
        return;
      }
      if (setTrasactionIfNotExists('setMicrophoneTrack') == false) {
        if (camInfo !== undefined) resetTransaction('setCameraTrack');
        callback(customErrors.error_1140);
        EL.warn('stream-event', customEvents.event_general_failed, { message: ('switchMediaDevices - Previous similar request in process') });

        return;
      }
    }
    let cam = {};
    if (camInfo != undefined) {
      if (typeof camInfo === 'string') cam.deviceId = camInfo;
      else if (typeof camInfo === 'object') {
        if (camInfo.deviceId != undefined && typeof camInfo.deviceId === 'string') cam.deviceId = camInfo.deviceId;
        else if (camInfo.facingMode != undefined && typeof camInfo.facingMode === 'string') { cam.facingMode = camInfo.facingMode; } else {
          Logger.error(`Error switchMediaDevices(), error: invalid camera options ${JSON.stringify(camInfo)}`);
          EL.error('stream-event', customEvents.event_general_failed, { message: (`Error switchMediaDevices(), error: invalid camera options ${JSON.stringify(camInfo)}`) });
          callback(customErrors.error_1155);
          return;
        }
      } else {
        Logger.error(`Error switchMediaDevices(), error: invalid camera options type ${JSON.stringify(camInfo)}`);
        EL.error('stream-event', customEvents.event_general_failed, { message: (`Error switchMediaDevices(), error: invalid camera options type ${JSON.stringify(camInfo)}`) });
        callback(customErrors.error_1155);
        return;
      }
    } else {
      cam = undefined;
    }

    if (Connection.getAndroidVersion() == 11 && Connection.browserEngineCheck() == 'chrome-stable') {
      callback(customErrors.error_1153);
      return;
    }
    if (that.stream == undefined) {
      callback(customErrors.error_1159);
      resetTransaction("setCameraTrack");
      return;
    }
    that.stream.getVideoTracks().forEach((track) => {
      track.stop();
      track.onended = setMediaDevicesTrack(stream, mic_id, cam, (status) => {
        var stream_status = false;
        var callback_stream = stream;
        stream.stream.getVideoTracks().forEach((track) => {
          stream_status = track.readyState;
        })
        if (status.result === 0) {
          attachStreamOnPlayerDivs(that.videoStream);
          resetTransaction("setCameraTrack");
          resetTransaction("setMicrophoneTrack");
          callback((status.result === 0) ? stream : status);
        }
        if (status.result === 1150) {
          attachStreamOnPlayerDivs(that.videoStream);

          callback(stream);
          if (stream_status == 'live') {
            attachStreamOnPlayerDivs(that.videoStream);
            resetTransaction("setCameraTrack");
            resetTransaction("setMicrophoneTrack");
            callback(stream);
          }
          else {
            resetTransaction("setCameraTrack");
            resetTransaction("setMicrophoneTrack");
            callback(status);

          }
        }
        else {
          resetTransaction("setCameraTrack");
          resetTransaction("setMicrophoneTrack");
          callback(status);
        }

      });
    });
  };

  that.setSpeaker = (speakerId) => {
    let id = that.getID();
    let elementID = that.setPlayerId();

    if (document.querySelector(`#${elementID.stream}`) !== undefined && document.querySelector(`#${elementID.stream}`) !== null) {
      let domIds = document.querySelectorAll('#' + elementID.stream);
      domIds.forEach(streamElement => {
        if (typeof streamElement.setSinkId === "function") {
          streamElement.setSinkId(speakerId);
        }
      });
    }
  };

  const setTrasactionIfNotExists = (transId) => {
    if (!that.transactions.get(transId)) {
      that.transactions.set(transId, { state: 'inprogress' });
      return true;
    }
    Logger.error(`Invalid state: previous request in progress. transId: ${transId}`);
    EL.warn('stream-event', customEvents.event_general_failed, { message: (`setTrasactionIfNotExists - Invalid state: previous request in progress. transId: ${transId}`) });
    return false;
  };

  const resetTransaction = transId => that.transactions.delete(transId);
  let retry = 0;

  const setMediaDevicesTrack = (stream, mic_id, cam, callback) => {
    //if(stream && stream.local===true && stream.pc && stream.pc.peerConnection &&
    if (stream && stream.local === true && !stream.ifScreen() && !stream.ifCanvas()) {
      const options = {};
      if (mic_id !== undefined) {
        if (mic_id !== 'none') options.audio = { deviceId: { exact: mic_id } };
        else options.audio = true;
      } else options.audio = false;
      if (cam !== undefined) {
        if (that.videoSize !== undefined && that.videoSize.length) {
          if (Connection.browserEngineCheck() === 'safari') {
            options.video = { width: that.videoSize[2], height: that.videoSize[3] };
          } else {
            options.video = { width: { max: that.videoSize[2] }, height: { max: that.videoSize[3] } };
            if (enxConfig.browser_info.device_type != 'mobile') {
              options.video.width.min = that.videoSize[0];
              options.video.height.min = that.videoSize[1];
            } else {
              options.video.width.ideal = that.videoSize[2];
              options.video.height.ideal = that.videoSize[3];
            }
          }
        }
        else {
          callback(customErrors.error_1190);
          return;
        }
        if (cam.deviceId != undefined && cam.deviceId !== "none")
          options.video = { 'deviceId': { exact: cam.deviceId } };
        if (cam.facingMode != undefined)
          options.video = { facingMode: { exact: cam.facingMode } };
        // options.video.facingMode = { exact: cam.facingMode};
        if (that.videoFrameRate !== undefined)
          options.video.frameRate = { min: that.videoFrameRate[0], max: that.videoFrameRate[1] };
      } else {
        options.video = false;
      }

      getUserMedia(options, (streamGot, error) => {
        if (error == undefined) {
          if (stream.pc && stream.pc.peerConnection) {
            replaceNewMediaStreamTrack(stream, (mic_id !== undefined), (cam !== undefined), streamGot);
          }
          stream.stream = streamGot;
          if (cam) {
            stream.videoStream = streamGot;
            if (cam.deviceId != undefined) {
              if (cam.deviceId !== 'none') spec.video.deviceId = cam.deviceId;
            } else {
              streamGot.getVideoTracks().forEach((track, index) => {
                if (track.getSettings().deviceId != undefined) spec.video.deviceId = track.getSettings().deviceId;
              });
            }
            Logger.log('Got video access from devices:- ', spec.video.deviceId);
            EL.info('stream-event', customEvents.event_gum_success, { message: (`Got video access from devices:- ${spec.video.deviceId}`) });
          }
          if (mic_id) {
            stream.audioStream = streamGot;
            if (mic_id !== 'none') {
              spec.audio.deviceId = mic_id;
            }
            Logger.log('Got audio access from devices:- ', mic_id);
            EL.info('stream-event', customEvents.event_gum_success, { message: (`Got audio access from devices:- ${mic_id}`) });
          }
          callback(customErrors.error_000);
        } else {
          Logger.info("Error getUserMedia(), error: " + error + " cam: " + cam.deviceId, " mic: ", mic_id);
          EL.error('stream-event', customEvents.event_gum_failed, { cam_id: cam.deviceId, mic_id: mic_id });
          callback(error);
        }
      });
    } else {
      Logger.info('Cannot switch devices over invalid stream');
      EL.error('stream-event', customEvents.event_general_failed, { message: ('setMediaDevicesTrack - Cannot switch devices over invalid stream') });
      callback(customErrors.error_1155);
    }
  };

  const replaceNewMediaStreamTrack = (streamObj, audio, video, newStream) => {
    let sender_audio_track_found = false,
      sender_video_track_found = false;
    streamObj.pc.peerConnection.getSenders().forEach((sender, index) => {
      if (sender.track) {
        if (video && (sender.track.kind === 'video')) {
          spec.video.trackIndex = index;
          sender_video_track_found = true;
          if (newStream) {
            newStream.getVideoTracks().forEach((vtrack) => {
              vtrack.enabled = true; sender.replaceTrack(vtrack);
            });
          } else {
            sender.replaceTrack(null);
          }
          streamObj.videoStream.getVideoTracks().forEach((vtrack) => {
            vtrack.onended = null; vtrack.stop();
          });
        } else if (audio && (sender.track.kind === 'audio')) {
          spec.audio.trackIndex = index;
          sender_audio_track_found = true;
          if (newStream) {
            newStream.getAudioTracks().forEach((atrack) => {
              atrack.enabled = true; sender.replaceTrack(atrack);
            });
          } else {
            sender.replaceTrack(null);
          }
          streamObj.audioStream.getAudioTracks().forEach((atrack) => {
            atrack.onended = null; atrack.stop();
          });
        }
      }
    });

    if ((audio && !sender_audio_track_found) || (video && !sender_video_track_found)) {
      streamObj.pc.peerConnection.getSenders().forEach((sender, index) => {
        if (!sender.track && newStream) {
          if (audio && !sender_audio_track_found && ((spec.audio.trackIndex !== undefined) && (index === spec.audio.trackIndex))) {
            sender_audio_track_found = true;
            newStream.getAudioTracks().forEach((atrack) => {
              atrack.enabled = true; sender.replaceTrack(atrack);
            });
          }
          if (video && !sender_video_track_found && ((spec.video.trackIndex !== undefined) && (index === spec.video.trackIndex))) {
            sender_video_track_found = true;
            newStream.getVideoTracks().forEach((vtrack) => {
              vtrack.enabled = true; sender.replaceTrack(vtrack);
            });
          }
        }
      });

      if ((audio && !sender_audio_track_found) || (video && !sender_video_track_found)) {
        Logger.error(`Error: Not finding sender track::  audio: ${audio} found: ${sender_audio_track_found
          } index: ${spec.audio.trackIndex} video: ${video} found: ${sender_video_track_found
          } index: ${spec.video.trackIndex}`);
        EL.error('stream-event', customEvents.event_general_failed, { message: (`replaceNewMediaStreamTrack - Not finding sender track::  audio: ${audio} found: ${sender_audio_track_found} index: ${spec.audio.trackIndex} video: ${video} found: ${sender_video_track_found} index: ${spec.video.trackIndex}`) });
        streamObj.pc.peerConnection.getSenders().forEach((sender, index) => {
          const kind = sender.track ? sender.track.kind : 'none';
          Logger.error(`index : ${index} sender.track: ${sender.track} kind : ${kind}`);
        });
      }
    }
    //streamObj.stream.getTracks().forEach(function(track){track.onended = null;track.stop();});
  };

  const shouldDisplayFrozenImage = (imageOnVideoMute, remoteClientId, userMute) => (imageOnVideoMute && ((currentRemoteClientId == remoteClientId) || (currentRemoteClientId == 'none')) &&
    ((currentMediaType == 'video') || (currentMediaType == 'audiovideo') || (currentImageDisplay && !userMute)));

  that.setPlayerId = function (room = null) {
    let id = that.getID();
    // console.log('Player id rooms: ', room, that.room);
    let _room = room ? room : that.room;
    let roomType = _room && _room.roomSettings && _room.roomSettings.room_type
      ? _room.roomSettings.room_type
      : null;

    let isBreakOutString = roomType === 'breakout-room' ? `br_${_room.roomID}_` : '';

    return {
      'stream': `${isBreakOutString}stream${id}`,
      'player': `${isBreakOutString}player_${id}`
    }
  }

  that.reloadPlayer = function (talkerInfo, imageOnVideoMute) {
    const newMediaType = talkerInfo.mediatype;
    const remoteClientId = talkerInfo.clientId;
    if ((currentMediaType == newMediaType) && !currentImageDisplay && (!imageOnVideoMute || currentVideoMuteReason == 'none' || currentVideoMuteReason == talkerInfo.reason)) {
      Logger.debug(`reloadPlayer() id : ${that.getID()} no change in media type: ${newMediaType}`);
      //if first time, set the muted options
      if (currentRemoteClientId === 'none') {
        switch (currentMediaType) {
          case 'audio':
          case 'audioOnly':
            audioMuted = false;
            videoMuted = true;
            break;
          case 'video':
            audioMuted = true;
            videoMuted = false;
            break;
          case 'audiovideo':
            audioMuted = videoMuted = false;
            break;
          default:
            audioMuted = videoMuted = true;
            break;
        }
      }
      currentRemoteClientId = remoteClientId;
      currentActiveTalkerList = {
        talkerInfo: JSON.parse(JSON.stringify(talkerInfo)),
        imageOnVideoMute,
      };
      return;
    }
    Logger.debug(`reloadPlayer() streamId: ${that.getID()} mediatype:( ${newMediaType} , ${currentMediaType} ), clientId:( ${remoteClientId} , ${currentRemoteClientId} ), reason:( ${talkerInfo.reason} , ${currentVideoMuteReason} ) ` +
      ` imageOnVideoMute: ${imageOnVideoMute}`);
    const id = that.getID();
    let elementID = that.setPlayerId();

    if (document.querySelector(`#${elementID.stream}`) !== undefined && document.querySelector(`#${elementID.stream}`) !== null) {
      var domIds = document.querySelectorAll('#' + elementID.stream);
      let stremAttached = false;

      domIds.forEach(streamElement => {
        try {
          switch (newMediaType) {
            case 'audio':
              //show prevous image incase of video mute TODO - if user mutes, show blank ?.
              currentVideoMuteReason = talkerInfo.reason;
              if (shouldDisplayFrozenImage(imageOnVideoMute, remoteClientId, (talkerInfo.reason == 'user'))) {
                //if transition from video-> audio, audio track need to be added
                if (that.mediaStream.getAudioTracks().length == 0) {
                  that.stream.getAudioTracks().forEach(function (track) {
                    that.mediaStream.addTrack(track);
                  });
                }
                currentImageDisplay = true;
                stremAttached = true;
                audioMuted = false;
                videoMuted = true;
                break;
              }
            case 'audioOnly':
              if (that.audioStream) {
                streamElement.srcObject = that.audioStream;
                currentImageDisplay = false;
                stremAttached = true;
                audioMuted = false;
                videoMuted = true;
              } else {
                Logger.error(`reloadPlayer() failed id : ${that.getID()} audiostream null ` +
                  `medtype - curr: ${currentMediaType} new: ${newMediaType}`);
              }
              break;
            case 'video':
              if (that.mediaStream) {
                if (currentMediaType == 'audiovideo') {
                  if (that.mediaStream.getVideoTracks().length == 0) {
                    that.stream.getVideoTracks().forEach(function (track) {
                      that.mediaStream.addTrack(track);
                    });
                  }
                  if (that.mediaStream.getAudioTracks().length != 0) {
                    that.mediaStream.getAudioTracks().forEach(function (track) {
                      that.mediaStream.removeTrack(track);
                    });
                  }
                } else {
                  streamElement.srcObject = that.mediaStream;
                }
                currentImageDisplay = false;
                currentVideoMuteReason = 'none';
                stremAttached = true;
                audioMuted = true;
                videoMuted = false;
              } else {
                Logger.error(`reloadPlayer() failed id : ${that.getID()} videostream null, medtype - curr: ${currentMediaType} new: ${newMediaType}`);
                EL.error('stream-event', customEvents.event_general_failed, { message: (`reloadPlayer() failed id : ${that.getID()} videostream null, medtype - curr: ${currentMediaType} new: ${newMediaType}`) });
              }
              break;
            case 'audiovideo':
              if (that.mediaStream) {
                if (that.mediaStream.getAudioTracks().length == 0) {
                  that.stream.getAudioTracks().forEach(function (track) {
                    that.mediaStream.addTrack(track);
                  });
                }
                if (that.mediaStream.getVideoTracks().length == 0) {
                  that.stream.getVideoTracks().forEach(function (track) {
                    that.mediaStream.addTrack(track);
                  });
                }
                if ((currentMediaType != 'video') || (Connection.browserEngineCheck() === 'safari')) {
                  streamElement.srcObject = that.mediaStream;
                }
                currentImageDisplay = false;
                currentVideoMuteReason = 'none';
                stremAttached = true;
                audioMuted = videoMuted = false;
              } else {
                Logger.error(`reloadPlayer() failed id : ${that.getID()} stream null, medtype - curr: ${currentMediaType} new: ${newMediaType}`);
                EL.error('stream-event', customEvents.event_general_failed, { message: (`reloadPlayer() failed id : ${that.getID()} stream null, medtype - curr: ${currentMediaType} new: ${newMediaType}`) });
              }
              break;
            default:
              currentVideoMuteReason = talkerInfo.reason;
              audioMuted = videoMuted = true;
              if (shouldDisplayFrozenImage(imageOnVideoMute, remoteClientId, (talkerInfo.reason == 'user'))) {
                currentImageDisplay = true;
                stremAttached = true;
                break;
              }
              streamElement.srcObject = null;
              currentImageDisplay = false;
              stremAttached = true;
              break;
          }
        } catch (error) {
          Logger.error(`reloadPlayer() id : ${that.getID()} NULL set medtype - curr: ${currentMediaType} new: ${newMediaType}`);
          EL.error('stream-event', customEvents.event_general_failed, { message: (`reloadPlayer() id : ${that.getID()} NULL set medtype - curr: ${currentMediaType} new: ${newMediaType}`) });
        }
      });
      if (stremAttached) {
        currentMediaType = newMediaType;
        currentRemoteClientId = remoteClientId;
      }
    } else {
      Logger.error(`reloadPlayer() id : ${that.getID()} ID selector null medtype - curr: ${currentMediaType} new: ${newMediaType} storing AT req`);
      EL.error('stream-event', customEvents.event_general_failed, { message: (`reloadPlayer() id : ${that.getID()} ID selector null medtype - curr: ${currentMediaType} new: ${newMediaType} storing AT req`) });
    }
    currentActiveTalkerList = {
      talkerInfo: JSON.parse(JSON.stringify(talkerInfo)),
      imageOnVideoMute,
    };
  };

  that.setBandwidthAlert = (arg) => {
    let lang;
    const streamId = that.getID();
    if (!that.player) { return; }
    if (that.abwdEnabled === true) {
      if (arg.bw === 'low') {
        if (that.abwdNotification === 'sdk') {
          that.player.setBwAlert(true, arg.type, arg.name);
        } else {
          //Fire a stream event to app.
          let Msg = '';
          if (arg.type === 'subscriber') {
            Msg = 'subscriber low';
          } else {
            Msg = `${arg.name} publisher low`;
          }
          console.log('Pusing the abwd event to server');
          const streamEvent = StreamEvent({
            type: 'bw-alert',
            stream: that,
            msg: Msg,
          });
          that.dispatchEvent(streamEvent);
        }
      } else if (that.abwdNotification === 'sdk') {
        that.player.setBwAlert(false);
      } else {
        const streamEvent = StreamEvent({
          type: 'bw-restored',
          stream: that,
        });
        console.log('Pusing the abwd restore event to server');
        that.dispatchEvent(streamEvent);
      }
    }
  };


  that.setVideoMutedMessage = (arg) => {
    if (that.player == null) return;
    if (arg.videomuted === true && arg.reason === 'bw') {
      that.videoMutedDueToBw = true;
      that.player.setVideoMutedMsg(arg);
    } else if (that.videoMutedDueToBw === true) {
      that.player.setVideoMutedMsg(arg);
    }
  };

  const attachStreamOnPlayerDivs = (stream2Attach) => {
    for (let i = 0; i < that.playerDivs.length; i++) {
      const elem = document.getElementById(that.playerDivs[i].elementId);
      if (elem) {
        const video_tag = elem.querySelectorAll('video');
        if (video_tag.length > 0) {
          const id = video_tag[0].id;
          const domIds = document.querySelectorAll(`#${id}`);
          domIds.forEach((streamElement) => {
            try {
              streamElement.srcObject = stream2Attach;
            } catch (error) {
              Logger.error(`Exception:: attachStreamOnPlayerDivs() streamId: ${that.getID()} error: ${error}`);
              EL.error('stream-event', customEvents.event_general_failed, { message: (`attachStreamOnPlayerDivs() streamId: ${that.getID()} error: ${error}`) });
            }
          });
        }
      }
    }
  };

  that.muteSubscriberStreamVideo = (enable) => {
    if (that.stream && !that.local) {
      that.stream.getVideoTracks().forEach((track) => {
        track.enabled = !enable;
      });
    }
  };

  //reconnection case
  that.updateVideo = (id) => {
    let elementID = that.setPlayerId();

    if (document.querySelector(`#${elementID.stream}`) !== undefined && document.querySelector(`#${elementID.stream}`) !== null) {
      Logger.info('Player element exists and playing the video', `${elementID.stream}`)
      that.htmlPlay(`${elementID.stream}`);
    } else {
      Logger.error('Player element does not exist');
      EL.error('stream-event', customEvents.event_general_failed, { message: ('updateVideo - Player element does not exist') });
    }
  };

  that.play = (elementID, optionsInput) => {
    if(typeof optionsInput === 'object' && optionsInput.hasOwnProperty('player'))
    {
      Logger.info(`Element ID is ${elementID}`);
      const options = optionsInput || {};
      that.elementID = elementID;
      let player;
      if (optionsInput.player && optionsInput.player.abwd) {
        if (optionsInput.player.abwd.enabled !== undefined) {
          that.abwdEnabled = optionsInput.player.abwd.enabled;
        }
        if (that.abwdEnabled === true) {
          if (optionsInput.player.abwd.notification) {
            that.abwdNotification = optionsInput.player.abwd.notification;
            if (that.abwdNotification === 'sdk') {
              if (optionsInput.player.abwd.language !== undefined) {
                if (optionsInput.player.abwd.language === 'en' || optionsInput.player.abwd.language === 'zh') {
                  that.abwdLanguage = optionsInput.player.abwd.language;
                } else {
                  that.abwdLanguage = 'en';
                }
              }
            }
          }
        }
      }
      //only one player is required. currently only Video player is used
      // TODO :remove both players and have single player - media player
      if ((that.ifVideo() || that.ifScreen()) || that.audioOnly || that.ifAudio()) {
        // Draw on HTML
        if (elementID !== undefined && (document.getElementById(elementID) !== null)) {
          /*this section of code is responsible to create and play the
          stream in dynamic video tag appended under <div></div>*/
          //-------------------------------------------
          if (Connection.browserEngineCheck() === 'IE') {
            const vid = document.getElementById(elementID);
            let video = document.getElementById(elementID);
            if (vid.nodeName !== 'VIDEO') {
              Logger.info('not video tag');
              video = document.createElement('video');
              video.setAttribute('height', vid.getAttribute('height'));
              video.setAttribute('width', vid.getAttribute('width'));
              vid.appendChild(video);
            }
            that.attachMediaStream(video, that.stream);
          } else {
            const extraPlayerOptions = { autoplay: true };
            if (Connection.browserEngineCheck() === 'safari') extraPlayerOptions.playsinline = true;
            player = VideoPlayer({
              id: that.getID(),
              stream: that, elementID,
              options, extraPlayerOptions,
              vroom: that.room ? that.room : null
            });
            if (that.local) player.video.muted = true;
            that.player = player;
            that.showing = true;
          }
          that.playerDivs = [];
          that.playerDivs.push({ elementId: elementID, options: optionsInput });
          currentMediaType = 'audiovideo';
          if (currentActiveTalkerList !== undefined) {
            Logger.error('stream.play currentActiveTalkerList : calling reload player()');
            that.reloadPlayer(currentActiveTalkerList.talkerInfo, currentActiveTalkerList.imageOnVideoMute);
          }
          if (!that.local && that.room != undefined && that.room.selectedSpeakerId != undefined) {
            that.setSpeaker(that.room.selectedSpeakerId);
          }
        } else {
          Logger.error('Element not found');
        }
      } else if (that.ifAudio() || that.audioOnly) {
        player = AudioPlayer({
          id: that.getID(),
          stream: that,
          elementID,
          options,
          vroom: that.room ? that.room : null
        });
        if (that.local) {
          player.audio.muted = true;
        }

        that.player = player;
        that.showing = true;
        that.playerDivs = [];
        that.playerDivs.push({ elementId: elementID, options: optionsInput });
        currentMediaType = 'audio';
      }
      if (that.reload) {
        delete that.reload;
      }
      if (that.room !== undefined) {
        // if stats are enabled the show the stats UI on player
        if (that.room.subscribeSessionStats === true) {
          that.ShowSessionStats(true);
        } else {
          that.ShowSessionStats(false);
        }
      } else {
        Logger.info('room is not allocated to stream , stats UI will not be shown');
      }
    }
    else{
      Logger.error('play() invalid param - callback/RoomDefinition');
    }
  };
  that.resetMediaConfiguration = function (mediaConfiguration) {
    that.pc.resetSDP(mediaConfiguration);
  };

  that.htmlPlay = (elementID, optionsInput) => {
    const options = optionsInput || {};
    that.elementID = elementID;
    let player;
    if (that.ifVideo() || that.ifScreen()) {
      // Draw on HTML
      if (elementID !== undefined) {
        /*this section of code is responsible to create and play the
        stream in dynamic video tag appended under <div></div>*/
        //-------------------------------------------
        /*pass the video tag Id here to play the stream in video tag*/
        //-----------------------------------------------------
        // reconnection case handle duplicate video
        /*    var domId=document.getElementById(elementID);
            //------------------------------------------------
                try {
                    domId.srcObject = that.stream;
                } catch (error) {
                    domId.src = URL.createObjectURL(that.stream);
                }
                domId.play().then(() => {
  
            }).catch((error) => {
            });*/

        const domIds = document.querySelectorAll(`#${elementID}`);
        domIds.forEach((streamElement) => {
          try {
            streamElement.srcObject = that.local ? that.videoStream : that.stream;
          } catch (error) {
            streamElement.src = URL.createObjectURL(that.local ? that.videoStream : that.stream);
          }
          streamElement.play().then(() => {

          }).catch((error) => {
            Logger.error(`Error playing video stream: ${error}`);
            EL.error('stream-event', customEvents.event_general_failed, { message: (`Error playing video stream: ${error}`) });
          });
        });
      }
    }
  };

  // ************************************
  // Public function: stop()
  // It destroys the player object if its already being played
  // ************************************

  that.stop = () => {
    if (that.showing) {
      if (that.player !== undefined) {
        that.player.destroy();
        that.showing = false;
      }
    }
  };

  that.show = that.play;
  that.hide = that.stop;
  /* ####this is the temporary mapping between the events call of#### */
  /*that.hasAudio = that.ifAudio;
  that.hasVideo = that.ifVideo;
  that.hasData = that.ifData;
  that.isExternal = that.ifExternal;
  that.hasMedia = that.ifMedia;
  that.hasScreen = that.ifScreen;*/


  /************************************
  //Author:Sohom
  //Description:To take a snapshot of video frame and to return a canvas object.
  // Public function: getVideoFrameCanvas()
  //
   ************************************/

  const getVideoFrameCanvas = (domHandle) => {
    if (that.player !== undefined && that.stream !== undefined) {
      const video = that.player.video;
      const style = document.defaultView.getComputedStyle(video);
      const width = parseInt(style.getPropertyValue('width'), 10);
      const height = parseInt(style.getPropertyValue('height'), 10);
      const left = parseInt(style.getPropertyValue('left'), 10);
      const top = parseInt(style.getPropertyValue('top'), 10);

      let div;
      if (typeof that.elementID === 'object' && typeof that.elementID.appendChild === 'function') {
        div = that.elementID;
      } else {
        div = document.getElementById(that.elementID);
      }

      const divStyle = document.defaultView.getComputedStyle(div);
      const divWidth = parseInt(divStyle.getPropertyValue('width'), 10);
      const divHeight = parseInt(divStyle.getPropertyValue('height'), 10);
      let canvas;
      if (!document.getElementById(domHandle)) {
        canvas = document.createElement('canvas');
        canvas.id = domHandle;
        canvas.width = divWidth;
        canvas.height = divHeight;
        canvas.className = 'canvas_input';
        canvas.setAttribute('style', 'position: absolute;top: 15%;left: 45%;z-index: 897;');
        document.body.appendChild(canvas);
      } else {
        canvas = document.getElementById(domHandle);
      }
      that.inputContext = canvas.getContext('2d');
      updateCanvas(height, width);
      return canvas;
    }
    return null;
  };

  const updateCanvas = (height, width) => {
    that.inputContext.drawImage(that.player.video, 0, 0, width, height);
    // requestAnimationFrame( updateCanvas );
  };

  /************************************
   * Author:Sohom
   * Description:
  // Public function: getVideoFrameURL()
  // To take a snapshot of video frame, the snapshot is saved and a URL is returned
   ************************************/

  that.getVideoFrameURL = (format) => {
    const canvas = getVideoFrameCanvas();
    if (canvas !== null) {
      if (format) {
        return canvas.toDataURL(...format);
      }
      return canvas.toDataURL();
    }
    return null;
  };


  /************************************
   * Author:Sohom
   * Description:
  // Public function: getVideoFrameImage()
  // To take a snapshot of video frame and to return an Image in raw format.
  ************************************/

  that.getVideoFrameImage = (domHandle) => {
    const canvas = getVideoFrameCanvas(domHandle);
    if (canvas !== null) {
      return canvas.getContext('2d').getImageData(10, 10, canvas.width, canvas.height);
    }
    return null;
  };


  // ************************************
  // Public function: checkOptions()
  // Check inconsistensy in Options/Attributes
  // ************************************

  that.checkOptions = (configInput, isUpdate) => {
    const config = configInput;

    // TODO: Check for any incompatible options
    if (isUpdate === true) { // We are updating the stream
      if (config.audio || config.screen) {
        Logger.warning('Error! Cannot update type of subscription');
        config.audio = undefined;
        config.screen = undefined;
      }
    } else if (that.local === false) { // check what we can subscribe to
      if (config.video === true && that.ifVideo() === false) {
        Logger.warning('Error! Stream doesn\'t have Video Track. Can\'t subscribe to Video');
        config.video = false;
      }
      if (config.audio === true && that.ifAudio() === false) {
        Logger.warning('Error! Stream doesn\'t have Audio Track. Can\'t subscribe to Audio');
        config.audio = false;
      }
    }
    if (that.local === false) {
      if (!that.ifVideo() && (config.slideShowMode === true)) {
        Logger.warning('Error! Stream doesn\'t have Video Track. Cannot enable slideShowMode. Check parameters');
        config.slideShowMode = false;
      }
    }
  };


  // ************************************
  // Public function: checkOptions()
  // Check inconsistensy in Options/Attributes
  // ************************************
  const muteStreamAudio = (enable, delay, callback = () => { }) => {
    if (!negotiatedMedia.audio) {
      EL.error('stream-event', customEvents.event_general_failed, { message: ('muteStreamAudio - Stream not found') });
      callback(customErrors.error_1159);
      return;
    }
    if (enable === audioMuted) {
      EL.error('stream-event', customEvents.event_general_failed, { message: ('muteStreamAudio - MediaDevice state is already in same state') });
      callback(customErrors.error_1177);
      return;
    }
    if (setTrasactionIfNotExists('setMicrophoneTrack') == false) {
      EL.error('stream-event', customEvents.event_general_failed, { message: ('muteStreamAudio - Previous similar request in process') });
      callback(customErrors.error_1140);
      return;
    }

    const setAudioTrack = (enable, callback) => {
      try {
        // replace track use for mute unmute is  not used as it creates avsync issue in recordings
        that.audioStream.getAudioTracks().forEach((track) => {
          track.enabled = !enable;
        });
        audioMuted = enable;
        resetTransaction('setMicrophoneTrack');
        EL.info('stream-event', customEvents.event_general_success, { message: ('muteStreamAudio - success') });
        callback(customErrors.error_000);

        /* if (enable){
          if (that.audioStream)
            that.audioStream.getAudioTracks().forEach(function(track){track.enabled = false;});
          replaceNewMediaStreamTrack (that, true, false,null);
          audioMuted = enable;
          callback(customErrors.error_000);
        }else{
          setMediaDevicesTrack(that, (spec.audio.deviceId ? spec.audio.deviceId : "none"), undefined,(status) => {
            if (status.result === customErrors.error_000.result) audioMuted = enable;
            callback(status);
          });
        }*/
      } catch (e) {
        Logger.error(`Excpetion happend : ${e}`);
        resetTransaction('setMicrophoneTrack');
        EL.error('stream-event', customEvents.event_general_failed, { message: (`muteStreamAudio - error - ${e}`) });
        callback(customErrors.error_1150);
      }
    };
    if (!delay) setAudioTrack(enable, callback);
    else setTimeout(setAudioTrack, delay, enable, callback);
  };

  const muteStreamVideo = (enable, delay, callback = () => { }) => {
    if (!negotiatedMedia.video) {
      EL.error('stream-event', customEvents.event_general_failed, { message: ('muteStreamVideo - Stream not found') });
      callback(customErrors.error_1159);
      return;
    }
    if (enable === videoMuted) {
      EL.error('stream-event', customEvents.event_general_failed, { message: ('muteStreamVideo - MediaDevice state is already in same state') });
      callback(customErrors.error_1177);
      return;
    }
    if (setTrasactionIfNotExists('setCameraTrack') == false) {
      callback(customErrors.error_1140);
      return;
    }
    const setVideoTrack = (enable, callback) => {
      try {
        if (enable) {
          replaceNewMediaStreamTrack(that, false, true, null);
          videoMuted = enable;
          resetTransaction('setCameraTrack');
          callback(customErrors.error_000);
        } else {
          const cam = {};
          if (spec.video.deviceId != undefined) cam.deviceId = spec.video.deviceId;
          else if (spec.video.facingMode !== undefined && enxConfig.browser_info.device_type == 'mobile') {
            cam.facingMode = spec.video.facingMode;
          } else {
            cam.deviceId = 'none';
          }
          setMediaDevicesTrack(that, undefined, cam, (status) => {
            if (status.result === customErrors.error_000.result) videoMuted = enable;
            resetTransaction('setCameraTrack');
            callback(status);
          });
        }
      } catch (e) {
        console.log(`Excpetion happend : ${e}`);
        resetTransaction('setCameraTrack');
        callback(customErrors.error_1150);
      }
    };
    if (!delay) setVideoTrack(enable, callback);
    else setTimeout(setVideoTrack, delay, enable, callback);
  };
  // ************************************
  // Public function: muteAudio()
  // To mute Audio at local end-point. Callback can be defined at application level
  // ************************************

  that.muteAudio = (callback = () => { }, raiseEvent = true, delay = 0, selfSet = true, paused = false) => {
    if (typeof callback === 'function') {
      console.log(`SelfSet:${selfSet}selfMuteAudio${that.selfMuteAudio}selfPausedAudio:${that.selfPausedAudio}`);
      if ((selfSet && that.selfMuteAudio) || (!selfSet && (that.hardAudioMuted || that.selfPausedAudio))) {
        if (paused === true) that.selfPausedAudio = true; //Just to make sure that room doesnt get unmuted in case of hard mute is turned off.
        callback(customErrors.error_1177);
        return;
      }
      muteStreamAudio(true, delay, (resp) => {
        if ((resp.result === customErrors.error_000.result) || (resp.result === customErrors.error_1177.result)) {
          if (selfSet) that.selfMuteAudio = true;
          else if (paused) that.selfPausedAudio = true;
          else that.hardAudioMuted = true;
          if (resp.result === customErrors.error_000.result && raiseEvent) that.sendEvent('user-audio-muted');
          else if (!raiseEvent) resp = customErrors.error_000; //already selfmuted but now remotemute and viceversa
        }
        callback(resp);
      });
    } else {
      Logger.error('muteAudio() invalid param - callback');
    }
  };
  that.setMediaDeviceMuteState = (isMuteOn, audio, video, raiseEvent, callback) => {
    try {
      if (audio) {
        if (isMuteOn === audioMuted) {
          callback(customErrors.error_1177);
          return;
        }
        /*
                  that.pc.peerConnection.getSenders().forEach(function(sender){
                    if(sender.track && sender.track.kind === 'audio'){
                      const parameters = sender.getParameters();
                      if ((parameters != undefined ) && (parameters.encodings != undefined)){
                        for (var cnt=0; cnt < parameters.encodings.length; cnt++){
                          Logger.error("setting encoding cnt " + !isMuteOn);
                          parameters.encodings[cnt].active = !isMuteOn;
                        }
                        sender.setParameters(parameters);
                      }
                    }
                  });*/
        that.audioStream.getAudioTracks().forEach((track) => { track.enabled = !isMuteOn; });
        audioMuted = isMuteOn;
        if (raiseEvent) that.sendEvent(audioMuted ? 'user-audio-muted' : 'user-audio-unmuted');
      }
      if (video) {
        if (isMuteOn === videoMuted) {
          callback(customErrors.error_1177);
          return;
        }
        that.stream.getVideoTracks().forEach((track) => { track.enabled = !isMuteOn; });
        videoMuted = isMuteOn;
        if (raiseEvent) that.sendEvent(videoMuted ? 'user-video-muted' : 'user-video-unmuted');
      }
      callback(customErrors.error_000);
    } catch (e) {
      console.log(`Excpetion happend : ${e}`);
      callback(customErrors.error_1150);
    }
  };

  that.muteAudioNotSelf = (callback = () => { }, raiseEvent) => {
    // that.sgelfMuteAudio = true;
    muteStreamAudio(true, 0, callback);
    if ((raiseEvent === undefined) || (raiseEvent !== false)) that.sendEvent('user-audio-muted');
  };

  const handleDataAvailable = (event) => {
    if (event.data && event.data.size > 0) {
      that.recordedBlobs.push(event.data);
    }
  };
  that.startLocalRecord = (callback = () => { }) => {
    if (!that.mediaRecorder) {
      try {
        //let options = {mimeType: 'video/webm;codecs=vp8'};
        const options = { mimeType: 'video/webm' };
        that.mediaRecorder = new MediaRecorder(that.stream, options);
        that.mediaRecorder.ondataavailable = handleDataAvailable;
        that.recordedBlobs = [];
        that.mediaRecorder.start(10);
        callback(customErrors.error_000);
      } catch (e) {
        Logger.error('Exception while creating MediaRecorder:', e);
        callback(customErrors.error_000);
      }
    } else {
      Logger.error('Recording started already:');
      callback(customErrors.error_1174);
    }
  };
  that.stopLocalRecord = (callback = () => { }) => {
    if (that.mediaRecorder && that.recordedBlobs.length > 0) {
      const today = new Date();
      const date = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
      const time = `${today.getHours()}:${today.getMinutes()}:${today.getSeconds()}`;
      const fileName = `stream_${that.getID()}_${date}::${time}.webm`;
      const blob = new Blob(that.recordedBlobs, { type: 'video/webm' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
      callback(customErrors.error_000);
    } else {
      callback(customErrors.error_1175);
    }
    that.mediaRecorder = undefined;
    that.recordedBlobs = [];
  };

  that.sendEvent = (type) => {
    if (that.room) {
      that.room.socket.sendEvent(type, (result) => {
      });
    }
  };
  that.sendMessage = (type, options, callback = () => { }) => {
    if (that.room) {
      that.room.socket.emitEvent(type, options, (result) => {
        callback(result);
      });
    }
  };

  // ************************************
  // Public function: unmuteAudio()
  // To unmute Audio at local end-point. Callback can be defined at application level
  // ************************************

  that.unmuteAudio = (callback = () => { }, raiseEvent = true, delay = 0, selfSet = true, paused = false) => {
    if (typeof callback === 'function') {
      console.log(`unmuteAudio selfSet : ${selfSet} that.selfMuteAudio: ${that.selfMuteAudio} that.hardAudioMuted: ${that.hardAudioMuted} audioMuted: ${audioMuted}paused:${that.selfPausedAudio}`);
      if ((selfSet && !that.selfMuteAudio && !that.hardAudioMuted && !that.selfPausedAudio) || (!selfSet && !(that.hardAudioMuted || that.selfPausedAudio))) {
        callback(customErrors.error_1177);
        return;
      }
      if (!selfSet || !that.hardAudioMuted || !that.selfPausedAudio || (that.room && that.room.isModerator())) {
        if (!selfSet && that.selfMuteAudio) {
          // if remote unmute but self muted, reset hard. retruning success
          that.hardAudioMuted = false;
          that.selfPausedAudio = false;
          callback(customErrors.error_000);
          return;
        }
        muteStreamAudio(false, delay, (resp) => {
          if ((resp.result === customErrors.error_000.result) || (resp.result === customErrors.error_1177.result)) {
            if (selfSet) that.selfMuteAudio = false;
            else if (paused) that.selfPausedAudio = false;
            else that.hardAudioMuted = false;
            if (resp.result === customErrors.error_000.result && raiseEvent) that.sendEvent('user-audio-unmuted');
            else if (!raiseEvent) resp = customErrors.error_000; //already selfunmuted but now remoteunmute and viceversa
          }
          callback(resp);
        });
      } else {
        callback(customErrors.error_1176);
      }
    } else {
      Logger.error('unmuteAudio() invalid param - callback');
    }
  };

  that.hardMuteAudio = (callback = () => { }) => {
    if (that.room && !that.local) {
      that.sendMessage('hardmute-user-audio', { clientId: that.clientId });
    }
  };
  that.hardUnmuteAudio = (callback = () => { }) => {
    if (that.room && !that.local) {
      that.sendMessage('hardunmute-user-audio', { clientId: that.clientId });
    }
  };
  that.hardMuteVideo = (callback = () => { }) => {
    if (that.room && !that.local) {
      that.sendMessage('hardmute-user-video', { clientId: that.clientId });
    }
  };
  that.hardUnmuteVideo = (callback = () => { }) => {
    if (that.room && !that.local) {
      that.sendMessage('hardunmute-user-video', { clientId: that.clientId });
    }
  };
  // ************************************
  // Public function: muteVideo()
  // To mute Video at local end-point. Callback can be defined at application level
  // ************************************
  that.muteNonATPubVideo = (callback = () => { }) => {
    if (that.videoStream) {
      that.ATVideoMuted = true;
      that.videoStream.getVideoTracks().forEach((track) => {
        track.enabled = false;
      });
    }
  };

  that.unmuteNonATPubVideo = (callback = () => { }) => {
    if (that.videoStream) {
      if (that.hardAudioMuted === false && that.selfMuteVideo === false && that.ATVideoMuted === true) {
        that.ATVideoMuted = false;
        that.videoStream.getVideoTracks().forEach((track) => {
          track.enabled = true;
        });
      }
    }
  };

  that.muteVideo = (callback = () => { }, raiseEvent = true, delay = 0, selfSet = true, onlySoftMute = false, pause = false) => {
    if (typeof callback === 'function') {
      console.log(`SelfSet:${selfSet}selfMuteVideo${that.selfMuteVideo}selfPausedVideo:${that.selfPausedVideo}`);
      if ((selfSet && that.selfMuteVideo) || (!selfSet && (that.hardVideoMuted || that.selfPausedVideo))) {
        if (pause) that.selfPausedVideo = true;
        callback(customErrors.error_1177);
        return;
      }
      if (that.pc === undefined) {
        that.videoStream.getVideoTracks().forEach((vtrack) => { vtrack.onended = null; vtrack.enabled = false; });
        that.config.videoMuted = true;
        callback(customErrors.error_000);
        return;
      }
      if (onlySoftMute) {
        that.videoStream.getVideoTracks().forEach((vtrack) => { vtrack.enabled = false; });
        that.softVideoMuted = true;
        callback(customErrors.error_000);
      } else {
        muteStreamVideo(true, delay, (resp) => {
          if ((resp.result === customErrors.error_000.result) || (resp.result === customErrors.error_1177.result)) {
            if (selfSet) that.selfMuteVideo = true;
            else if (pause) that.selfPausedVideo = true;
            else that.hardVideoMuted = true;
            if (resp.result === customErrors.error_000.result) {
              attachStreamOnPlayerDivs(null);
              if (raiseEvent) that.sendEvent('user-video-muted');
            } else resp = customErrors.error_000; //already selfmuted but now remotemute and viceversa
          }
          callback(resp);
        });
      }
    } else {
      Logger.error('muteVideo() invalid param - callback');
    }
  };

  // ************************************
  // Public function: unmuteVideo()
  // To unmute Video at local end-point. Callback can be defined at application level
  // ************************************

  that.unmuteVideo = (callback = () => { }, raiseEvent = true, delay = 0, selfSet = true, pause = false) => {
    if (typeof callback === 'function') {
      {
        console.log(`unmuteVideoo selfSet : ${selfSet} that.selfMuteVideo: ${that.selfMuteVideo} that.hardVideoMuted: ${that.hardVideoMuted}paused:${that.selfPausedAudio}`);
        if (that.config.videoMuted === true && that.pc === undefined) {
          that.videoStream.getVideoTracks().forEach((vtrack) => {
            vtrack.enabled = true;
          });
          that.config.videoMuted = false;
          callback(customErrors.error_000);
          return;
        }
        if ((selfSet && !that.selfMuteVideo && !that.hardVideoMute && !that.selfPausedVideo) || (!selfSet && !(that.hardVideoMuted || that.selfPausedVideo))) {
          callback(customErrors.error_1177);
          return;
        }
        if (!selfSet || !that.hardVideoMuted || !that.selfPausedVideo || (that.room && that.room.isModerator())) {
          if (!selfSet && that.selfMuteVideo) {
            // if remote unmute happens but self muted, just respond success and reset hard flag
            that.hardVideoMuted = false;
            if (pause) that.selfPausedVideo = false;
            callback(customErrors.error_000);
            return;
          }
          muteStreamVideo(false, delay, (resp) => {
            if ((resp.result === customErrors.error_000.result) || (resp.result === customErrors.error_1177.result)) {
              that.softVideoMuted = false;
              if (selfSet) that.selfMuteVideo = false;
              else if (pause) that.selfPausedVideo = false;
              else that.hardVideoMuted = false;
              if (resp.result === customErrors.error_000.result) {
                attachStreamOnPlayerDivs(that.videoStream);
                if (raiseEvent) that.sendEvent('user-video-unmuted');
              } else resp = customErrors.error_000; //already selfunmuted but now remoteunmute and viceversa
            }
            callback(resp);
          });
        } else {
          callback(customErrors.error_1176);
        }
      }
    } else {
      Logger.error('unmuteVideo() invalid param - callback');
    }
  };


  // ************************************
  // Public function: _setStaticQualityLayer()
  // eslint-disable-next-line no-underscore-dangle
  // ************************************

  that._setStaticQualityLayer = (spatialLayer, temporalLayer, callback = () => { }) => {
    if (that.room && that.room.p2p) {
      Logger.warning('Error! p2p streams don\'t support setStaticQualityLayer');
      callback('error');
      return;
    }

    const config = { qualityLayer: { spatialLayer, temporalLayer } };
    that.checkOptions(config, true);
    that.pc.updateSpec(config, callback);
  };


  // ************************************
  // Public function: _setDynamicQualityLayer()
  // eslint-disable-next-line no-underscore-dangle
  // ************************************

  that._setDynamicQualityLayer = (callback) => {
    if (that.room && that.room.p2p) {
      Logger.warning('Error! p2p streams don\'t support setDynamicQualityLayer');
      callback('error');
      return;
    }
    const config = { qualityLayer: { spatialLayer: -1, temporalLayer: -1 } };
    that.checkOptions(config, true);
    that.pc.updateSpec(config, callback);
  };


  // ************************************
  // Function: controlHandler()
  // eslint-disable-next-line no-underscore-dangle
  // ************************************

  const controlHandler = (handlersInput, publisherSideInput, enable) => {
    let publisherSide = publisherSideInput;
    let handlers = handlersInput;
    if (publisherSide !== true) {
      publisherSide = false;
    }

    handlers = (typeof handlers === 'string') ? [handlers] : handlers;
    handlers = (handlers instanceof Array) ? handlers : [];

    if (handlers.length > 0) {
      that.room.sendControlMessage(that, 'control', {
        name: 'controlhandlers',
        enable,
        publisherSide,
        handlers,
      });
    }
  };


  // ************************************
  // Function: disableHandlers()
  // desc:
  // ************************************

  that.disableHandlers = (handlers, publisherSide) => {
    controlHandler(handlers, publisherSide, false);
  };


  // ************************************
  // Function: enableHandlers()
  // desc:
  // ************************************

  that.enableHandlers = (handlers, publisherSide) => {
    controlHandler(handlers, publisherSide, true);
  };


  that.updateSimulcastLayersBitrate = (bitrates, byServer) => {
    if (that.pc && that.local) {
      that.pc.updateSimulcastLayersBitrate(bitrates);
      if (byServer) {
        config.maxVideoLayeredBitrates = bitrates;
      }
    }
  };

  // ************************************
  // Public Function: updateConfiguration()
  // desc: To update configuraiton of stream on peer connection
  // ************************************

  that.updateConfiguration = (config, callback = () => { }) => {
    if (config === undefined) { return; }
    if (that.pc) {
      that.checkOptions(config, true);
      if (that.local) {
        if (that.room.p2p) {
          for (let index = 0; index < that.pc.length; index += 1) {
            that.pc[index].updateSpec(config, callback);
          }
        } else if (config.maxVideoBW != undefined || config.minVideoBW != undefined ||
          config.maxVideoFps != undefined || config.minVideoFps != undefined) {
          if (that.maxVideoLayers <= 1) {
            console.log(`updateConfiguration id: ${that.getID()} config:: ${JSON.stringify(config)}`);
            that.setVideoParamsRange(
              config.maxVideoBW, config.minVideoBW,
              config.maxVideoFps, config.minVideoFps, false,
            );
          }
        } else {
          that.pc.updateSpec(config, callback);
        }
      } else {
        that.pc.updateSpec(config, callback);
      }
    } else {
      callback('Error! This stream has no peerConnection attached, Skipping');
    }
  };

  that.ShowSessionStats = (statsEnabled) => {
    if (statsEnabled === true) {
      if (that.player !== undefined) {
        that.player.ShowSessionStats(true);
      } else {
        Logger.info('stream does not have a player to show stats');
      }
    } else if (that.player !== undefined) {
      that.player.ShowSessionStats(false);
    } else {
      Logger.info('stream does not have a player to hide stats');
    }
  };

  /**
   * Replace track method
   */

  that.replaceLocalTrack = (localStreamObject, newStream) => {

    localStreamObject.pc.peerConnection.getSenders().forEach((sender, index) => {
      if (sender.track) {
        if (sender.track.kind === 'video') {
          if (newStream) {
            newStream.getVideoTracks().forEach((vtrack) => {
              console.log(vtrack);
              vtrack.enabled = true;
              sender.replaceTrack(vtrack);
            });
          }

        }
        else if (sender.track.kind === 'audio') {
          if (localStreamObject) {
            localStreamObject.audioStream.getAudioTracks().forEach((atrack) => {
              console.log(atrack);
              atrack.enabled = true;
              sender.replaceTrack(atrack);
            });
          }
        }
      }
    });
  }

  return that;
};
export default Stream;



//////////////////
// WEBPACK FOOTER
// ./src/Stream.js
// module id = 15
// module chunks = 0